import { useState, useEffect, useCallback } from 'react';
import { inventoryAPI, clinicAdminAPI, billingAPI, inventoryBulkAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import SearchableSelect from '../components/SearchableSelect';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  Package, MagnifyingGlass, Plus, ArrowsLeftRight, Buildings, CurrencyInr,
  WarningCircle, CaretDown, CaretUp, PencilSimple, Trash, Eye, UploadSimple,
} from '@phosphor-icons/react';

const fmt = (n) => new Intl.NumberFormat('en-IN', { maximumFractionDigits: 2 }).format(n || 0);

export default function InventoryPage() {
  const [tab, setTab] = useState('dashboard');
  return (
    <div className="space-y-6" data-testid="inventory-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold font-['Plus_Jakarta_Sans'] text-[#0B1B3D]">Inventory & Billing</h1>
          <p className="text-sm text-gray-500 font-['Plus_Jakarta_Sans']">Manage products, stock, clinic transfers, and billing</p>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-white border border-gray-200 rounded-xl p-1" data-testid="inventory-tabs">
          <TabsTrigger value="dashboard" className="rounded-lg data-[state=active]:bg-[#0B1B3D] data-[state=active]:text-white">Dashboard</TabsTrigger>
          <TabsTrigger value="catalog" className="rounded-lg data-[state=active]:bg-[#0B1B3D] data-[state=active]:text-white">Item Catalog</TabsTrigger>
          <TabsTrigger value="stock" className="rounded-lg data-[state=active]:bg-[#0B1B3D] data-[state=active]:text-white">Stock & Locations</TabsTrigger>
          <TabsTrigger value="transfers" className="rounded-lg data-[state=active]:bg-[#0B1B3D] data-[state=active]:text-white">Transfers</TabsTrigger>
          <TabsTrigger value="clinics" className="rounded-lg data-[state=active]:bg-[#0B1B3D] data-[state=active]:text-white">Clinic Accounts</TabsTrigger>
          <TabsTrigger value="billing" className="rounded-lg data-[state=active]:bg-[#0B1B3D] data-[state=active]:text-white">Billing Overview</TabsTrigger>
        </TabsList>

        <TabsContent value="dashboard"><DashboardTab /></TabsContent>
        <TabsContent value="catalog"><CatalogTab /></TabsContent>
        <TabsContent value="stock"><StockTab /></TabsContent>
        <TabsContent value="transfers"><TransfersTab /></TabsContent>
        <TabsContent value="clinics"><ClinicsTab /></TabsContent>
        <TabsContent value="billing"><BillingOverviewTab /></TabsContent>
      </Tabs>
    </div>
  );
}

/* ─── Dashboard ─────────────────────────────────────────── */
function DashboardTab() {
  const [data, setData] = useState(null);
  useEffect(() => { inventoryAPI.getDashboard().then(r => setData(r.data)).catch(console.error); }, []);
  if (!data) return <div className="text-center py-12 text-gray-400">Loading...</div>;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <SCard label="Total Items" val={data.total_items} sub={`${data.goods_count} goods, ${data.services_count} services`} />
        <SCard label="Stock Value (Cost)" val={`₹${fmt(data.total_stock_value_cost)}`} sub={`${fmt(data.total_stock_units)} units`} />
        <SCard label="Stock Value (Sell)" val={`₹${fmt(data.total_stock_value_sell)}`} sub="At selling price" />
        <SCard label="Potential Margin" val={`₹${fmt(data.potential_margin)}`} sub="Sell - Cost" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Low Stock */}
        <Card className="border border-gray-200 rounded-2xl">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-['Plus_Jakarta_Sans'] text-[#0B1B3D] flex items-center gap-2">
              <WarningCircle size={18} className="text-amber-500" /> Low Stock Alerts
            </CardTitle>
          </CardHeader>
          <CardContent>
            {data.low_stock_items.length === 0 ? (
              <p className="text-sm text-gray-400 py-4 text-center">All stock levels healthy</p>
            ) : (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {data.low_stock_items.map((item, i) => (
                  <div key={i} className="flex justify-between items-center p-2 rounded-lg bg-amber-50 border border-amber-100">
                    <div>
                      <p className="text-sm font-medium text-[#0B1B3D]">{item.name}</p>
                      <Badge variant="outline" className="text-xs">{item.category}</Badge>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-semibold text-amber-700">{item.stock_on_hand} left</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Categories */}
        <Card className="border border-gray-200 rounded-2xl">
          <CardHeader className="pb-2"><CardTitle className="text-base font-['Plus_Jakarta_Sans'] text-[#0B1B3D]">Categories</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-2">
              {(data.categories || []).map((cat, i) => (
                <div key={i} className="flex justify-between items-center p-2 rounded-lg hover:bg-gray-50">
                  <span className="text-sm font-medium capitalize">{cat._id || 'Uncategorized'}</span>
                  <div className="text-right text-sm">
                    <span className="text-gray-600">{cat.count} items</span>
                    <span className="ml-3 text-gray-400">₹{fmt(cat.stock_value)}</span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Location Stock */}
      {data.location_stock.length > 0 && (
        <Card className="border border-gray-200 rounded-2xl">
          <CardHeader className="pb-2"><CardTitle className="text-base font-['Plus_Jakarta_Sans'] text-[#0B1B3D]">Stock by Location</CardTitle></CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {data.location_stock.map((loc, i) => (
                <div key={i} className="border border-gray-100 rounded-xl p-3">
                  <p className="text-xs text-gray-500 truncate">{loc._id}</p>
                  <p className="text-lg font-semibold text-[#0B1B3D]">{loc.total_quantity}</p>
                  <p className="text-xs text-gray-400">{loc.total_items} items</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function SCard({ label, val, sub }) {
  return (
    <Card className="border border-gray-200 rounded-2xl">
      <CardContent className="p-4">
        <p className="text-xs text-gray-500 mb-1">{label}</p>
        <p className="text-xl font-semibold font-['Plus_Jakarta_Sans'] text-[#0B1B3D]">{val}</p>
        <p className="text-xs text-gray-400">{sub}</p>
      </CardContent>
    </Card>
  );
}

/* ─── Catalog ───────────────────────────────────────────── */
function CatalogTab() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const [categories, setCategories] = useState([]);
  const [sortBy, setSortBy] = useState('name');
  const [sortDir, setSortDir] = useState(1);
  const [editItem, setEditItem] = useState(null);
  const [uploadDialog, setUploadDialog] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);

  const load = useCallback(async () => {
    try {
      const params = { page, page_size: 50, search, sort_by: sortBy, sort_dir: sortDir };
      if (category) params.category = category;
      const { data } = await inventoryAPI.getItems(params);
      setItems(data.items);
      setTotal(data.total);
    } catch (e) { console.error(e); }
  }, [page, search, category, sortBy, sortDir]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { inventoryAPI.getCategories().then(r => setCategories(r.data.categories)).catch(() => {}); }, []);

  const handleBulkUpload = async () => {
    if (!uploadFile) { toast.error('Select a file'); return; }
    setUploading(true);
    setUploadResult(null);
    try {
      const formData = new FormData();
      formData.append('file', uploadFile);
      const { data } = await inventoryBulkAPI.upload(formData);
      setUploadResult(data);
      toast.success(`Created ${data.created} items, skipped ${data.skipped}`);
      if (data.created > 0) load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Upload failed'); }
    finally { setUploading(false); }
  };

  const toggleSort = (field) => {
    if (sortBy === field) setSortDir(d => d * -1);
    else { setSortBy(field); setSortDir(1); }
  };

  const SortIcon = ({ field }) => {
    if (sortBy !== field) return null;
    return sortDir === 1 ? <CaretUp size={12} /> : <CaretDown size={12} />;
  };

  return (
    <div className="space-y-4" data-testid="catalog-tab">
      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <MagnifyingGlass size={16} className="absolute left-3 top-3 text-gray-400" />
          <Input data-testid="catalog-search" placeholder="Search items..." value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }} className="pl-9 rounded-xl" />
        </div>
        <SearchableSelect value={category} onChange={v => { setCategory(v === 'all' ? '' : v); setPage(1); }} placeholder="All Categories" testid="catalog-category-filter" buttonClassName="w-48"
                  options={[{ value: 'all', label: 'All Categories' }, ...categories.map(c => ({ value: c, label: c }))]} />
        <Button onClick={() => setUploadDialog(true)} className="bg-[#0B1B3D] hover:bg-[#16274D] text-white rounded-xl" data-testid="bulk-upload-btn">
          <UploadSimple size={16} className="mr-1" /> Bulk Upload
        </Button>
      </div>

      {/* Bulk Upload Dialog */}
      <Dialog open={uploadDialog} onOpenChange={setUploadDialog}>
        <DialogContent className="max-w-md" data-testid="bulk-upload-dialog">
          <DialogHeader>
            <DialogTitle>Bulk Upload Inventory Items</DialogTitle>
            <DialogDescription>
              Upload an Excel (.xlsx) or CSV file with columns: name, sku, category, unit, hsn_code, purchase_price, selling_price, gst_rate, description
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Input type="file" accept=".xlsx,.xls,.csv" onChange={e => { setUploadFile(e.target.files[0]); setUploadResult(null); }} data-testid="bulk-upload-file-input" />
            {uploadResult && (
              <div className="bg-[#F8FAFC] rounded-xl p-3 text-sm space-y-1">
                <p className="font-medium">Upload Result:</p>
                <p className="text-green-700">Created: {uploadResult.created}</p>
                <p className="text-amber-700">Skipped (duplicates): {uploadResult.skipped}</p>
                {uploadResult.errors?.length > 0 && <p className="text-red-600">Errors: {uploadResult.errors.join(', ')}</p>}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setUploadDialog(false); setUploadFile(null); setUploadResult(null); }}>Close</Button>
            <Button onClick={handleBulkUpload} disabled={uploading || !uploadFile} className="bg-[#0B1B3D] hover:bg-[#16274D]" data-testid="confirm-bulk-upload-btn">
              {uploading ? 'Uploading...' : 'Upload & Import'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <p className="text-sm text-gray-500">{total} items</p>

      <Card className="border border-gray-200 rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 text-left">
                <th className="p-3 font-medium text-gray-600 cursor-pointer" onClick={() => toggleSort('name')}>
                  <span className="flex items-center gap-1">Name <SortIcon field="name" /></span>
                </th>
                <th className="p-3 font-medium text-gray-600">Category</th>
                <th className="p-3 font-medium text-gray-600">Type</th>
                <th className="p-3 font-medium text-gray-600 text-right cursor-pointer" onClick={() => toggleSort('rate')}>
                  <span className="flex items-center justify-end gap-1">Rate <SortIcon field="rate" /></span>
                </th>
                <th className="p-3 font-medium text-gray-600 text-right">Cost</th>
                <th className="p-3 font-medium text-gray-600 text-right cursor-pointer" onClick={() => toggleSort('stock_on_hand')}>
                  <span className="flex items-center justify-end gap-1">Stock <SortIcon field="stock_on_hand" /></span>
                </th>
                <th className="p-3 font-medium text-gray-600">GST %</th>
                <th className="p-3 font-medium text-gray-600 text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map(item => (
                <tr key={item.id} className="border-t border-gray-50 hover:bg-gray-50/50" data-testid={`catalog-item-${item.id}`}>
                  <td className="p-3">
                    <p className="font-medium text-[#0B1B3D]">{item.name}</p>
                    {item.sku && <p className="text-xs text-gray-400">SKU: {item.sku}</p>}
                  </td>
                  <td className="p-3"><Badge variant="outline" className="text-xs capitalize">{item.category || '—'}</Badge></td>
                  <td className="p-3 text-xs capitalize text-gray-600">{item.product_type}</td>
                  <td className="p-3 text-right font-medium">₹{fmt(item.rate)}</td>
                  <td className="p-3 text-right text-gray-500">₹{fmt(item.purchase_rate)}</td>
                  <td className="p-3 text-right">
                    <span className={`font-medium ${(item.stock_on_hand || 0) <= 5 ? 'text-amber-600' : 'text-[#0B1B3D]'}`}>
                      {item.stock_on_hand || 0}
                    </span>
                  </td>
                  <td className="p-3 text-gray-600">{item.tax_percentage || 0}%</td>
                  <td className="p-3 text-center">
                    <Button variant="ghost" size="sm" onClick={() => setEditItem(item)} data-testid={`edit-item-${item.id}`}>
                      <PencilSimple size={16} />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {total > 50 && (
        <div className="flex justify-center gap-2">
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="rounded-xl">Prev</Button>
          <span className="text-sm text-gray-500 px-3 py-2">Page {page} of {Math.ceil(total / 50)}</span>
          <Button variant="outline" size="sm" disabled={page * 50 >= total} onClick={() => setPage(p => p + 1)} className="rounded-xl">Next</Button>
        </div>
      )}

      {editItem && (
        <EditItemDialog item={editItem} onClose={() => setEditItem(null)} onSaved={() => { setEditItem(null); load(); }} />
      )}
    </div>
  );
}

/* ─── Edit Item Dialog ─────────────────────────────────── */
function EditItemDialog({ item, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: item.name || '',
    sku: item.sku || '',
    category: item.category || '',
    product_type: item.product_type || 'goods',
    rate: item.rate || 0,
    purchase_rate: item.purchase_rate || 0,
    tax_percentage: item.tax_percentage || 0,
    hsn_code: item.hsn_code || '',
    description: item.description || '',
    unit: item.unit || 'pcs',
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await inventoryAPI.updateItem(item.id, {
        name: form.name,
        sku: form.sku || undefined,
        category: form.category || undefined,
        product_type: form.product_type,
        rate: Number(form.rate),
        purchase_rate: Number(form.purchase_rate),
        tax_percentage: Number(form.tax_percentage),
        hsn_code: form.hsn_code || undefined,
        description: form.description || undefined,
        unit: form.unit || undefined,
      });
      toast.success('Item updated');
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to update item'); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={o => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="edit-item-dialog">
        <DialogHeader><DialogTitle>Edit: {item.name}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1 block">Item Name *</label>
              <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                className="rounded-xl" data-testid="edit-item-name" />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1 block">SKU</label>
              <Input value={form.sku} onChange={e => setForm(f => ({ ...f, sku: e.target.value }))}
                className="rounded-xl" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1 block">Category</label>
              <Input value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))}
                className="rounded-xl" />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1 block">Type</label>
              <Select value={form.product_type} onValueChange={v => setForm(f => ({ ...f, product_type: v }))}>
                <SelectTrigger className="rounded-xl"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="goods">Goods</SelectItem>
                  <SelectItem value="service">Service</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1 block">Selling Rate (₹)</label>
              <Input type="number" value={form.rate} onChange={e => setForm(f => ({ ...f, rate: e.target.value }))}
                className="rounded-xl" data-testid="edit-item-rate" />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1 block">Purchase/Cost (₹)</label>
              <Input type="number" value={form.purchase_rate} onChange={e => setForm(f => ({ ...f, purchase_rate: e.target.value }))}
                className="rounded-xl" />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1 block">GST %</label>
              <Input type="number" value={form.tax_percentage} onChange={e => setForm(f => ({ ...f, tax_percentage: e.target.value }))}
                className="rounded-xl" data-testid="edit-item-gst" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1 block">HSN Code</label>
              <Input value={form.hsn_code} onChange={e => setForm(f => ({ ...f, hsn_code: e.target.value }))}
                className="rounded-xl" />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1 block">Unit</label>
              <Input value={form.unit} onChange={e => setForm(f => ({ ...f, unit: e.target.value }))}
                className="rounded-xl" />
            </div>
          </div>
          <div>
            <label className="text-xs font-medium text-gray-600 mb-1 block">Description</label>
            <Input value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              placeholder="Optional description" className="rounded-xl" />
          </div>
        </div>
        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving || !form.name}
            className="bg-[#0B1B3D] text-white rounded-xl" data-testid="save-item-btn">
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ─── Stock ─────────────────────────────────────────────── */
function StockTab() {
  const [locations, setLocations] = useState([]);
  const [selectedLoc, setSelectedLoc] = useState('');
  const [stock, setStock] = useState([]);
  const [items, setItems] = useState({});
  const [uploadDialog, setUploadDialog] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);

  useEffect(() => { inventoryAPI.getLocations().then(r => setLocations(r.data.locations)).catch(() => {}); }, []);

  const loadStock = useCallback(async (loc) => {
    if (!loc) return;
    setSelectedLoc(loc);
    try {
      const { data } = await inventoryAPI.getStock({ location: loc });
      setStock(data.stock);
      const ids = data.stock.map(s => s.item_id);
      const itemMap = {};
      for (const id of ids) {
        if (!items[id]) {
          try {
            const r = await inventoryAPI.getItem(id);
            itemMap[id] = r.data;
          } catch { itemMap[id] = { name: id }; }
        }
      }
      setItems(prev => ({ ...prev, ...itemMap }));
    } catch (e) { console.error(e); }
  }, [items]);

  const handleBulkStockUpload = async () => {
    if (!uploadFile) return;
    setUploading(true);
    setUploadResult(null);
    try {
      const formData = new FormData();
      formData.append('file', uploadFile);
      const { data } = await inventoryBulkAPI.uploadStock(formData);
      setUploadResult(data);
      toast.success(`Updated ${data.updated} stock entries, ${data.created_items} new items`);
      inventoryAPI.getLocations().then(r => setLocations(r.data.locations)).catch(() => {});
      if (selectedLoc) loadStock(selectedLoc);
    } catch (e) { toast.error(e.response?.data?.detail || 'Upload failed'); }
    finally { setUploading(false); }
  };

  return (
    <div className="space-y-4" data-testid="stock-tab">
      <div className="flex gap-3 items-center">
        <SearchableSelect value={selectedLoc} onChange={loadStock} placeholder="Select location" testid="stock-location-filter" buttonClassName="w-72"
                  options={[...locations.map(l => ({ value: l, label: l }))]} />
        <Button onClick={() => setUploadDialog(true)} className="bg-[#0B1B3D] hover:bg-[#16274D] text-white rounded-xl ml-auto" data-testid="bulk-stock-upload-btn">
          <UploadSimple size={16} className="mr-1" /> Bulk Upload Stock
        </Button>
      </div>

      {/* Bulk Stock Upload Dialog */}
      <Dialog open={uploadDialog} onOpenChange={setUploadDialog}>
        <DialogContent className="max-w-md" data-testid="bulk-stock-upload-dialog">
          <DialogHeader>
            <DialogTitle>Bulk Upload Stock & Locations</DialogTitle>
            <DialogDescription>
              Upload Excel/CSV with stock data. Supports serial number files (product_name, serial_number_code, line_item_location_name) or aggregated format (item_name, location, quantity).
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Input type="file" accept=".xlsx,.xls,.csv" onChange={e => { setUploadFile(e.target.files[0]); setUploadResult(null); }} data-testid="stock-upload-file-input" />
            {uploadResult && (
              <div className="bg-[#F8FAFC] rounded-xl p-3 text-sm space-y-1">
                <p className="font-medium">Upload Result:</p>
                <p className="text-green-700">Stock entries updated: {uploadResult.updated}</p>
                <p className="text-blue-700">New items created: {uploadResult.created_items}</p>
                {uploadResult.serial_numbers_tracked > 0 && (
                  <p className="text-[#0B1B3D] font-medium">Serial numbers tracked: {uploadResult.serial_numbers_tracked?.toLocaleString()}</p>
                )}
                {uploadResult.unique_products > 0 && (
                  <p className="text-[#64748B]">{uploadResult.unique_products} products across {uploadResult.unique_locations} locations</p>
                )}
                {uploadResult.skipped > 0 && <p className="text-amber-700">Skipped: {uploadResult.skipped}</p>}
                <p className="text-[#64748B] text-xs">Total rows processed: {uploadResult.total_rows?.toLocaleString()}</p>
                {uploadResult.errors?.length > 0 && <p className="text-red-600 text-xs">Errors: {uploadResult.errors.slice(0,3).join('; ')}</p>}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setUploadDialog(false); setUploadFile(null); setUploadResult(null); }}>Close</Button>
            <Button onClick={handleBulkStockUpload} disabled={uploading || !uploadFile} className="bg-[#0B1B3D] hover:bg-[#16274D]" data-testid="confirm-stock-upload-btn">
              {uploading ? 'Processing...' : 'Upload Stock'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {selectedLoc && (
        <Card className="border border-gray-200 rounded-2xl">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-['Plus_Jakarta_Sans'] text-[#0B1B3D]">{selectedLoc} — {stock.length} items</CardTitle>
          </CardHeader>
          <CardContent>
            {stock.length === 0 ? (
              <p className="text-sm text-gray-400 py-4 text-center">No stock at this location</p>
            ) : (
              <div className="space-y-2">
                {stock.map((s, i) => (
                  <div key={i} className="flex justify-between items-center p-3 rounded-xl border border-gray-100 hover:bg-gray-50">
                    <div>
                      <p className="text-sm font-medium text-[#0B1B3D]">{items[s.item_id]?.name || s.item_id}</p>
                      <p className="text-xs text-gray-400">{items[s.item_id]?.category || ''}</p>
                    </div>
                    <p className={`text-lg font-semibold ${s.quantity <= 0 ? 'text-red-500' : 'text-[#0B1B3D]'}`}>{s.quantity}</p>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

/* ─── Transfers ─────────────────────────────────────────── */
function TransfersTab() {
  const [locations, setLocations] = useState([]);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [items, setItems] = useState([]);
  const [selectedItem, setSelectedItem] = useState('');
  const [qty, setQty] = useState(1);
  const [notes, setNotes] = useState('');
  const [isDemo, setIsDemo] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const [itemSearch, setItemSearch] = useState('');

  useEffect(() => {
    inventoryAPI.getLocations().then(r => setLocations(r.data.locations)).catch(() => {});
    inventoryAPI.getItems({ page_size: 200, product_type: 'goods' }).then(r => setItems(r.data.items)).catch(() => {});
  }, []);

  const handleTransfer = async () => {
    if (!from || !to || !selectedItem || qty <= 0) return;
    setSubmitting(true); setMessage('');
    try {
      await inventoryAPI.transferStock({ item_id: selectedItem, from_location: from, to_location: to, quantity: qty, notes, is_demo: isDemo });
      setMessage('Transfer successful!');
      setQty(1); setNotes('');
    } catch (e) {
      setMessage(e.response?.data?.detail || 'Transfer failed');
    } finally { setSubmitting(false); }
  };

  const filteredItems = items.filter(i => i.name?.toLowerCase().includes(itemSearch.toLowerCase()));

  return (
    <div className="space-y-6" data-testid="transfers-tab">
      <Card className="border border-gray-200 rounded-2xl">
        <CardHeader className="pb-2"><CardTitle className="text-base font-['Plus_Jakarta_Sans'] text-[#0B1B3D]">Create Transfer</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          {message && <div className={`text-sm p-3 rounded-xl ${message.includes('success') ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-600'}`}>{message}</div>}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1 block">From</label>
              <SearchableSelect value={from} onChange={setFrom} placeholder="Source" testid="transfer-from" buttonClassName="w-full"
                  options={[...locations.map(l => ({ value: l, label: l }))]} />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1 block">To</label>
              <Select value={to} onValueChange={setTo}>
                <SelectTrigger className="rounded-xl" data-testid="transfer-to"><SelectValue placeholder="Destination" /></SelectTrigger>
                <SelectContent>{locations.filter(l => l !== from).map(l => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <label className="text-xs font-medium text-gray-600 mb-1 block">Item</label>
            <Input placeholder="Search items..." value={itemSearch} onChange={e => setItemSearch(e.target.value)} className="rounded-xl mb-2" />
            <Select value={selectedItem} onValueChange={setSelectedItem}>
              <SelectTrigger className="rounded-xl" data-testid="transfer-item"><SelectValue placeholder="Select item" /></SelectTrigger>
              <SelectContent>
                {filteredItems.slice(0, 50).map(i => <SelectItem key={i.id} value={i.id}>{i.name} (Stock: {i.stock_on_hand})</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1 block">Quantity</label>
              <Input type="number" value={qty} onChange={e => setQty(Number(e.target.value))} className="rounded-xl" min="1" data-testid="transfer-qty" />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1 block">Notes</label>
              <Input value={notes} onChange={e => setNotes(e.target.value)} placeholder="Optional notes" className="rounded-xl" />
            </div>
          </div>
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={isDemo} onChange={e => setIsDemo(e.target.checked)} className="rounded" />
              Demo unit
            </label>
            <Button data-testid="execute-transfer-btn" onClick={handleTransfer} disabled={submitting || !from || !to || !selectedItem}
              className="bg-[#0B1B3D] hover:bg-[#16274D] text-white rounded-xl">
              <ArrowsLeftRight size={16} className="mr-2" /> {submitting ? 'Transferring...' : 'Execute Transfer'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

/* ─── Clinics ───────────────────────────────────────────── */
function ClinicsTab() {
  const [clinics, setClinics] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const { data } = await clinicAdminAPI.listClinics();
      setClinics(data.clinics);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const resetPw = async (id, code) => {
    if (!window.confirm(`Reset password for ${code} to default?`)) return;
    try {
      await clinicAdminAPI.resetPassword(id);
      alert('Password reset to: hearclear@clinic');
    } catch (e) { alert('Reset failed'); }
  };

  if (loading) return <div className="text-center py-12 text-gray-400">Loading clinics...</div>;

  return (
    <div className="space-y-4" data-testid="clinics-tab">
      <p className="text-sm text-gray-500">{clinics.length} clinic accounts</p>
      <Card className="border border-gray-200 rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 text-left">
                <th className="p-3 font-medium text-gray-600">Code</th>
                <th className="p-3 font-medium text-gray-600">Clinic Name</th>
                <th className="p-3 font-medium text-gray-600">State</th>
                <th className="p-3 font-medium text-gray-600">Staff</th>
                <th className="p-3 font-medium text-gray-600">GSTIN</th>
                <th className="p-3 font-medium text-gray-600">Status</th>
                <th className="p-3 font-medium text-gray-600">Actions</th>
              </tr>
            </thead>
            <tbody>
              {clinics.map(c => (
                <tr key={c.id} className="border-t border-gray-50 hover:bg-gray-50/50" data-testid={`clinic-row-${c.code}`}>
                  <td className="p-3 font-mono font-semibold text-[#0B1B3D]">{c.code}</td>
                  <td className="p-3 font-medium">{c.name}</td>
                  <td className="p-3 text-gray-600">{c.state || '—'}</td>
                  <td className="p-3 text-center">{c.employee_count || 0}</td>
                  <td className="p-3 text-xs text-gray-500 font-mono">{c.gstin || '—'}</td>
                  <td className="p-3">
                    <Badge variant={c.is_active ? 'default' : 'outline'}
                      className={c.is_active ? 'bg-emerald-100 text-emerald-700 border-emerald-200' : 'text-red-600'}>
                      {c.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                  </td>
                  <td className="p-3">
                    <Button variant="ghost" size="sm" onClick={() => resetPw(c.id, c.code)} className="text-xs text-gray-500 hover:text-[#0B1B3D]">
                      Reset Password
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

/* ─── Billing Overview (Admin) ──────────────────────────── */
function BillingOverviewTab() {
  const [invoices, setInvoices] = useState([]);
  const [revenue, setRevenue] = useState(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [search, setSearch] = useState('');
  const [clinicFilter, setClinicFilter] = useState('');
  const [clinics, setClinics] = useState([]);

  useEffect(() => { clinicAdminAPI.listClinics().then(r => setClinics(r.data.clinics)).catch(() => {}); }, []);

  const load = useCallback(async () => {
    try {
      const params = { page, page_size: 30 };
      if (fromDate) params.from_date = fromDate;
      if (toDate) params.to_date = toDate;
      if (search) params.search = search;
      if (clinicFilter) params.clinic_code = clinicFilter;
      const [invRes, revRes] = await Promise.all([
        billingAPI.adminGetInvoices(params),
        billingAPI.adminGetRevenue({ from_date: fromDate, to_date: toDate }),
      ]);
      setInvoices(invRes.data.invoices);
      setTotal(invRes.data.total);
      setRevenue(revRes.data);
    } catch (e) { console.error(e); }
  }, [page, fromDate, toDate, search, clinicFilter]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-6" data-testid="billing-overview-tab">
      {/* Revenue Summary */}
      {revenue && (
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
          <SCard label="Total Revenue" val={`₹${fmt(revenue.overall?.total_revenue)}`} sub={`${revenue.overall?.total_invoices || 0} invoices`} />
          <SCard label="Total GST" val={`₹${fmt(revenue.overall?.total_tax)}`} sub="Across all clinics" />
          <SCard label="Active Clinics" val={revenue.by_clinic?.length || 0} sub="With billing data" />
        </div>
      )}

      {/* Clinic Breakdown */}
      {revenue?.by_clinic?.length > 0 && (
        <Card className="border border-gray-200 rounded-2xl">
          <CardHeader className="pb-2"><CardTitle className="text-base font-['Plus_Jakarta_Sans'] text-[#0B1B3D]">Revenue by Clinic</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-2">
              {revenue.by_clinic.map((c, i) => (
                <div key={i} className="flex justify-between items-center p-3 rounded-xl border border-gray-100">
                  <div>
                    <p className="font-medium text-sm text-[#0B1B3D]">{c.clinic_name}</p>
                    <p className="text-xs text-gray-400">{c.clinic_code} | {c.total_invoices} invoices</p>
                  </div>
                  <p className="font-semibold text-[#0B1B3D]">₹{fmt(c.total_revenue)}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <MagnifyingGlass size={16} className="absolute left-3 top-3 text-gray-400" />
          <Input placeholder="Search invoices..." value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }} className="pl-9 rounded-xl" />
        </div>
        <SearchableSelect value={clinicFilter || ''} onChange={v => { setClinicFilter(v); setPage(1); }}
          options={[{ value: '', label: 'All Clinics' }, ...clinics.map(c => ({ value: c.code, label: `${c.code} - ${c.name}` }))]}
          placeholder="All Clinics" testid="inv-clinic-filter" buttonClassName="w-48 rounded-xl" />
        <Input type="date" value={fromDate} onChange={e => { setFromDate(e.target.value); setPage(1); }} className="rounded-xl w-40" />
        <Input type="date" value={toDate} onChange={e => { setToDate(e.target.value); setPage(1); }} className="rounded-xl w-40" />
      </div>

      <p className="text-sm text-gray-500">{total} invoices</p>

      {/* Invoice List */}
      <div className="space-y-2">
        {invoices.map(inv => (
          <Card key={inv.id} className="border border-gray-100 rounded-xl">
            <CardContent className="p-4 flex items-center justify-between">
              <div>
                <p className="font-medium text-sm text-[#0B1B3D] font-['Plus_Jakarta_Sans']">{inv.invoice_number}</p>
                <p className="text-xs text-gray-500">{inv.customer?.name} | {inv.audiologist?.name || '—'}</p>
                <p className="text-xs text-gray-400">{inv.clinic_name} | {inv.date}</p>
              </div>
              <div className="text-right">
                <p className="font-semibold text-[#0B1B3D]">₹{fmt(inv.grand_total)}</p>
                <Badge variant="outline" className="text-xs capitalize">{inv.payment_mode}</Badge>
              </div>
            </CardContent>
          </Card>
        ))}
        {invoices.length === 0 && <p className="text-center text-gray-400 py-8">No invoices found</p>}
      </div>

      {total > 30 && (
        <div className="flex justify-center gap-2">
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="rounded-xl">Prev</Button>
          <span className="text-sm text-gray-500 px-3 py-2">Page {page}</span>
          <Button variant="outline" size="sm" disabled={page * 30 >= total} onClick={() => setPage(p => p + 1)} className="rounded-xl">Next</Button>
        </div>
      )}
    </div>
  );
}
